"use client";
import { SponsorBannerSlot, nativeRows } from "@/components/ads/positions";

import { ListingHeader } from "./listing-header";
import { ListingCard } from "./listing-card";
import { VideoCard } from "../video-card";
import { SidebarAdRail, SidebarPopularRail } from "./listing-sidebar";
import { useInfiniteList } from "./use-infinite-list";
import { InfiniteSentinel } from "./infinite-sentinel";
import type { ArticleListItem } from "@/fixtures/ej-health/medical-news-fixtures";
import type { SidebarItem } from "@/fixtures/ej-health/fixtures";

interface Props {
  heading: string;
  /**
   * `"videos"` for the two video categories — 醫生會客室 and 動出健康, where
   * every article IS a video. They render the same `VideoCard` the home page
   * and the video detail page use (play glyph, duration chip, 會員專享 flag) in
   * a 3-up grid, rather than an article card with a still image.
   */
  variant?: "articles" | "videos";
  eyebrow?: string;
  description?: string;
  showFollow?: boolean;
  items: ArticleListItem[];
  popularList: SidebarItem[];
  relatedTags?: string[];
}

// First page sits beside the ad rail; infinite-loaded items append beside the
// popular rail.
// 12 cards (six 2-up rows) on first paint so the 3rd, 6th and 9th cards — the GAM
// InBanner native positions — all render before the reader scrolls; 6 more per load.
const FIRST_BLOCK = 12;
const PAGE_SIZE = 6;

export function ListingPage({
  heading,
  variant = "articles",
  eyebrow,
  description,
  showFollow,
  items,
  popularList,
  relatedTags,
}: Props) {
  const { visible, hasMore, sentinelRef } = useInfiniteList(
    items,
    PAGE_SIZE,
    FIRST_BLOCK,
  );

  const firstBatch = visible.slice(0, FIRST_BLOCK);
  const moreBatch = visible.slice(FIRST_BLOCK);

  return (
    <section className="mx-auto w-full max-w-[1280px] px-6 py-10 lg:px-10 lg:py-14">
      <div className="flex flex-col gap-12">
        <ListingHeader
          heading={heading}
          eyebrow={eyebrow}
          showFollow={showFollow}
          description={description}
        />
        {/* SponsorBanner1 — sponsor logo beside the listing header */}
        <SponsorBannerSlot label="廣告　Ad" />

        {/* Block 1 — first page of articles beside the ad rail */}
        <div className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_300px]">
          <ItemGrid variant={variant} items={firstBatch} />

          <SidebarAdRail />
        </div>

        {/* Block 2 — infinite-loaded articles beside the popular rail */}
        <div className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div className="flex flex-col gap-10">
            {moreBatch.length > 0 && (
              <ItemGrid
                variant={variant}
                items={moreBatch}
                keyOffset={FIRST_BLOCK}
              />
            )}

            <InfiniteSentinel
              sentinelRef={sentinelRef}
              hasMore={hasMore}
              doneText="已顯示全部文章"
            />
          </div>

          <SidebarPopularRail popularList={popularList} relatedTags={relatedTags} />
        </div>
      </div>
    </section>
  );
}

/** The two grids differ in shape as well as card: articles are a 2-up of wide
 *  cards, videos a 3-up of 16:9 tiles — the same grid the 醫生會客室 listing
 *  uses. */
function ItemGrid({
  variant,
  items,
  keyOffset = 0,
}: {
  variant: "articles" | "videos";
  items: ArticleListItem[];
  keyOffset?: number;
}) {
  if (variant === "videos") {
    return (
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
        {items.map((item, i) => (
          <VideoCard
            key={item.title + (keyOffset + i)}
            video={{
              category: item.category ?? "",
              time: item.time,
              title: item.title,
              // Every video fixture carries one; the fallback keeps the chip
              // from rendering the string "undefined" if one ever does not.
              duration: item.duration ?? "",
              img: item.img,
              subscriberOnly: item.subscriberOnly,
            }}
          />
        ))}
      </div>
    );
  }
  return (
    <div className="grid gap-x-7 gap-y-10 sm:grid-cols-2">
      {/* Every 3rd card is the GAM InBanner native position; the card itself is the fallback. */}
      {nativeRows(items.map((item, i) => (
        <ListingCard key={item.title + (keyOffset + i)} item={item} />
      )), 3)}
    </div>
  );
}
